import { useSelector } from 'react-redux';
import useResponsive from './helpers/hooks/useResponsive';
import { createTheme, useMediaQuery } from '@material-ui/core';
import { green, yellow, red, blue, orange, pink, purple, teal, grey, cyan } from '@material-ui/core/colors';

export function createCustomTheme(
  mainBackgroundColor,
  mainPaperColor,
  accentColor,
  mainPrimaryColor,
  mainPrimaryColorDark,
  mainSecondaryColor,
  mainSecondaryColorDark,
) {
  const { isXSmallScreen, isSmallScreen, isMediumScreen } = useResponsive();
  const darkState = useSelector((store) => store.gui.darkState);

  const smallDrawer = isXSmallScreen || isSmallScreen || isMediumScreen;

  let myTheme = createTheme({
    typography: {
      fontFamily: ['Inter', 'sans-serif'],
    },

    palette: {
      type: darkState ? 'dark' : 'light',

      background: {
        default: darkState ? mainBackgroundColor : '#EEF2F6',
        paper: darkState ? mainPaperColor : '#FFFFFF',
        lighterBg: darkState ? '#585858' : '#C4BCBC',
        mainLight: darkState ? '#696969' : '#EEEEEE',
        highlightLight: darkState ? '#4d4d4d' : '#c1c1c1',
        highlightDarker: darkState ? '#5b5b5b' : '#e3e3e3',
        mainDark: darkState ? '#424242' : '#FFFFFF',
        darkerBg: darkState ? '#303030' : '#d1d1d1',
      },

      primary: {
        main: mainPrimaryColor,
        dark: mainPrimaryColorDark,
        accent: accentColor,
      },

      secondary: {
        main: mainSecondaryColor,
        dark: mainSecondaryColorDark,
        accent: accentColor,
      },

      // Brand-adjacent accent colors
      accents: {
        main: '#46b0b0',
        dark: '#425f5f',
        light: '#298a8a',
      },

      // Status colors
      status: {
        notEvaluated: grey[400],
        warning: '#ff9800',
      },

      diagnosticsClasses: {
        // BI-RADS classifications
        birads1: green[500],
        birads2: green[800],
        birads3: yellow[800],
        birads4: orange[600],
        birads5: red[600],

        // Other Findings types
        lesionKnown: '#5da4b8',
        priorFinding: '#4ea9c5',
        vessel: '#4DAA86',

        // Growth/change categories
        decrease: green[600],
        stable: green[500],
        slow: yellow[800],
        moderate: orange[600],
        fast: red[600],

        // Other categories
        noEval: grey[400],
      },

      qualityClasses: {
        // 🌿 GREEN
        perfect: green[600],
        good: green[500],
        level_1: green[600],
        parenchymaCorrect: green[500], // #47ba5d ≈ green[500]
        noSkinFolds: green[300],
        adequate: green[500],
        acrA: green[500],
        acrB: green[600], // #60aa4d ≈ green[600]
        compOptimal: green[500],
        nippleStraight: green[500],
        correct: green[500], // #3ab719 ≈ green[700]
        risk_low: green[500],
        volumeUp: green[500],
        symmetryCorrect: green[500],
        buckyCorrect: green[500],
        blur_ok: green[500],
        slow: green[600],
        straight: green[500],

        // 💛 YELLOW / ORANGE
        moderate: yellow[700],
        level_2: yellow[700],
        parenchymaModerate: yellow[800], // #ca9c39 ≈ yellow[800]
        skinFoldsMild: yellow[600], // #c1c643 ≈ yellow[600]
        acrC: orange[600], // #ff9800 = orange[600]
        compHigh: orange[600],
        nippleTiltUp: orange[600],
        nippleTiltDown: yellow[800], // #a9a540 ≈ yellow[800]
        risk_moderate: orange[600],
        symmetryModerate: orange[600],
        mild: yellow[600],
        limited: orange[600],
        medium: orange[600],
        unsuspicious: orange[600],
        unsuspiciousLight: orange[200], // #ffb4008f ≈ lighter orange
        cutsModerate: orange[600],
        skinFoldsMedium: orange[600],

        // 🔴  RED
        insufficient: red[600],
        level_3: red[600],
        skinFoldsSevereProj: red[700], // #bf4037 ≈ red[700]
        report_insufficient: red[400], // #ff4c3f ≈ red[400]
        high: red[400],
        low: red[400],
        fast: red[400],
        convex: red[400],
        concave: red[400],
        inadequate: red[400],
        suspicious: red[400],
        parenchymaInsufficient: red[400],
        skinFoldsSevere: red[400],
        nippleTiltDownSevere: red[400],
        cutsInsufficient: red[400],
        risk_high: red[400],
        acrD: red[400],
        compLow: red[400],
        volumeDown: red[400],
        symmetryInsufficient: red[400],
        buckyInsufficient: red[400],
        blur_insufficient: red[400],

        // 🎨 OTHER COLORS
        none: grey[400],
        parenchyma: cyan[400], // #06FCF6 ≈ cyan[400]
        parenchymaProj: teal[300], // #47cfcbb0 ≈ teal[300]
        clips: blue[700], // #0675FC ≈ blue[700]
        skinFolds: purple[200], // #C88AFF ≈ purple[200]
        scars: purple[400], // #be3ea2 ≈ purple[400]
        pectoralis: pink[200], // #FF1493 ≈ pink[500]
        pectoralisProj: pink[400], // #dd1c84 ≈ pink[300]
        blur: pink[200], // 'pink' ≈ pink[200]
        cutsNoEval: blue[200], // #719aa5 ≈ blue-grey, closest blue[200]
        rcc: blue[600], // #2a75ca ≈ blue[600]
        lcc: blue[200],
        rmlo: blue[500], // #606dc1 ≈ indigo/blue[500]
        lmlo: purple[300], // #a071af ≈ purple[300]
        optionOne: blue[800], // #377193 ≈ blue[800]
        optionTwo: teal[800], // #2c7075 ≈ teal[800]
        not_evaluated: grey[500],
        second_captures: grey[900], // #1a2a32 ≈ very dark grey
        no: grey[400],
        postop: '#4272c9',
      },

      densityClasses: {
        none: grey[400],
        acrA: green[600],
        acrB: green[500],
        acrC: yellow[800],
        acrD: red[600],
      },

      riskClasses: {
        none: grey[400],
        low: green[800],
        moderate: yellow[700],
        high: red[600],
      },

      projections: {
        rcc: '#2a75ca',
        lcc: '#719aa5',
        rmlo: '#606dc1',
        lmlo: '#a071af',
        perfect: '#47ba5d',
        good: '#60aa4d',
        moderate: '#ff9800',
        adequate: '#47ba5d',
        inadequate: mainPrimaryColor,
        optionOne: '#377193',
        optionTwo: '#2c7075',
        suspicious: mainPrimaryColor,
        unsuspicious: '#ff9800',
        unsuspiciousLight: '#ffb4008f',
        none: '#1f8604',
        acrA: '#47ba5d',
        acrB: '#60aa4d',
        acrC: '#ff9800',
        acrD: mainPrimaryColor,
        compLow: mainPrimaryColor,
        compOptimal: '#47ba5d',
        compHigh: '#ff9800',
        nippleTiltUpSevere: mainPrimaryColor,
        nippleTiltUp: '#ff9800',
        nippleStraight: '#47ba5d',
        nippleTiltDown: '#a9a540',
        nippleTiltDownSevere: mainPrimaryColor,
        not_evaluated: 'gray',
        risk_high: mainPrimaryColor,
        risk_moderate: '#ff9800',
        risk_low: '#47ba5d',
        volumeUp: '#47ba5d',
        volumeDown: mainPrimaryColor,
        symmetryCorrect: '#47ba5d',
        symmetryModerate: '#ff9800',
        symmetryInsufficient: mainPrimaryColor,
        buckyCorrect: '#47ba5d',
        buckyInsufficient: mainPrimaryColor,
        blur_ok: '#47ba5d',
        blur_insufficient: mainPrimaryColor,
        report_insufficient: '#ff4c3f',
        insufficient: '#ff4c3f',
        correct: '#47ba5d',
        blue: '#4272c9',
      },

      dashboard: {
        perfect: '#108d10',
        adequate: '#60AA4D',
        good: '#60AA4D',
        mild: '#399c5e',
        moderate: '#ff6b00',
        inadequate: '#CC4238',
        insufficient: '#CC4238',
        none: '#4e4e4e',
        noSkinFolds: '#6ca782',
        acrA: '#108d10',
        acrB: '#60AA4D',
        acrC: '#ff6b00',
        acrD: '#CC4238',
      },

      texts: {
        primary: darkState ? '#d4d4d4' : '#212121',
        secondary: darkState ? '#d4d4d4' : '#414141',
        helper: 'gray',
        white: '#ffffff',
        generalTexts: darkState ? '#FFFFFF' : '#4E4E4E',
        generalTitleBackgrounds: darkState ? '#595959' : '#b9b9b9',
      },
    },

    fonts: {
      responsiveLarge: 'min(6.5vh, 8rem)',
      responsiveHeader: 'min(2.4vh, 1.5rem)',
      responsiveBig: 'min(1.9vh, 1.4rem)',
      responsiveMediumBig: 'min(1.4vh, 1.2rem)',
      responsiveMedium: 'min(1.2vh, 1rem)',
      responsiveMediumSmall: 'min(0.8vw, 12px)',
      responsiveSmall: 'min(1vh, 0.9rem)',
      responsiveXSmall: 'min(0.875rem, 8px)',
      responsiveText: 'max(0.875rem, 12px)',
      responsiveTextSmall: 'max(0.775rem, 11px)',
    },

    zIndex: {
      main_drawer: 1284,
      nav_menu: 1500,
      backdrops_drawer: 1300,
      backdrops: 2000,
      chips: 400,
    },

    drawerWidth: 380,
    drawerWidthAvatar: smallDrawer ? 310 : 360,
    drawerWidthMenu: '64px',
  });

  myTheme.overrides = {
    MuiCssBaseline: {
      '@global': {
        '*::-webkit-scrollbar': {
          width: '0.4em',
          scrollBehavior: 'smooth',
        },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: grey[600],
          borderRadius: '5px',
        },
      },
    },

    MuiButton: {
      root: {
        fontSize: myTheme.fonts.responsiveMedium,
        borderRadius: '5px',
      },
      containedSizeSmall: {
        fontSize: myTheme.fonts.responsiveMedium,
        borderRadius: '5px',
      },
      outlinedSizeSmall: {
        fontSize: myTheme.fonts.responsiveMedium,
        borderRadius: '5px',
      },
      iconSizeMedium: {
        '& > *:first-child': {
          fontSize: myTheme.fonts.responsiveBig,
        },
      },
    },
  };

  // Apply responsiveFontSizes here if you want
  // myTheme = responsiveFontSizes(myTheme);

  return myTheme;
}
